import { useCallback, useEffect, useRef } from 'react'
import { StackableFusuma } from '~/components/modal/StackableFusuma'
import { CancelGuard } from '~/features/reservationInput/card/CancelGuard'
import { CardFusuma } from '~/features/reservationInput/card/CardFusuma'
import { OptionMenu } from '~/features/reservationInput/optionMenu/OptionMenu'
import { OptionMenusFusuma } from '~/features/reservationInput/optionMenu/OptionMenuFusuma'
import { TotalCountFusuma } from '~/features/reservationInput/totalCount/TotalCount'
import type { PlanId, RestaurantId } from '~/lib/graphql/scalars'
import { useReserveOverlay } from '~/navigation'
import { css, sva } from '~/styled-system/css'
import { ChildMenusFusuma } from '../childMenu'
import { useLoginProps } from '../store/reserve'
import { scope } from '../store/scope'
import { useIsLoading } from '../store/stateMachine/state'
import { useMarkUserInteracted } from '../store/validate'
import { InitAtom } from './InitAtom'
import { Loading } from './Loading'
import { AffiliateSiteForm } from './affiliateSiteForm'
import { Agreement } from './agreement'
import { CancelPolicyFusuma } from './cancelPolicy'
import { DiscountCoupon } from './discountCoupon/DiscountCoupon'
import { GivenPoints } from './givenPoints/GivenPoints'
import { IkyuOnlyIkyuPointsUsage } from './ikyuPointsUsage/IkyuPointsUsage'
import { IkyuLogin } from './login/IkyuLogin'
import { MailMagazineSubscription } from './mailMagazineSubscription'
import { NotesFusuma } from './notes'
import { PayPayPointsUsage } from './payPayPointsUsage/PayPayPointsUsage'
import { PaymentAmountDetails } from './paymentAmountDetails'
import { PaymentMethod } from './paymentMethod/PaymentMethod'
import { PlanContents } from './plan'
import { CourseDetailModal } from './plan/CourseDetailModal'
import { PointAndDiscountCouponUnavailable } from './pointAndDiscountCouponUnavailable'
import { RequestsAndComments } from './requestsAndComments/RequestsAndComments'
import { ReservationConfirmation } from './reservationConfirmation/ReservationConfirmation'
import { ReservationError } from './reservationError'
import { ReservationSuccessFusuma } from './reservationSuccess'
import { RestaurantContents } from './restaurantContents'
import { SeatDetailModal, SeatsSelect } from './seatsSelect'
import { UserInput } from './userInput/UserInput'
import { IkyuOnlyYidLoginMessage } from './yidLoginMessage'

export function ReserveInput() {
  const [context, willBeUnmounted] = useReserveOverlay(
    'NEW_RESERVE_INPUT',
    'NEW_RESERVE_SUCCESS',
    'NEW_RESERVE_ERROR'
  )
  if (context === undefined) {
    return null
  }
  const { restaurantId, planId } = context
  return (
    <scope.Provider>
      <RequestBeacon restaurantId={restaurantId} planId={planId} />
      <StackableFusuma title="ご予約情報の入力・確認" willBeUnmounted={willBeUnmounted}>
        <Body />
      </StackableFusuma>
      <ChildMenusFusuma />
      <TotalCountFusuma />
      <CourseDetailModal />
      <SeatDetailModal />
      <OptionMenusFusuma />
      <CardFusuma />
      <CancelPolicyFusuma />
      <NotesFusuma />
      <ReservationError />
      <ReservationSuccessFusuma />
      <InitAtom context={context} />
    </scope.Provider>
  )
}

// クライアントナビゲーションでも発火するように明示的に叩く
export function RequestBeacon({
  restaurantId,
  planId,
}: { restaurantId: RestaurantId; planId: PlanId }) {
  useEffect(() => {
    fetch(`/${restaurantId}/plan${planId}/reservation/beacon`)
  }, [restaurantId, planId])
  return null
}

const styles = sva({
  slots: ['container', 'information', 'plan', 'sub'],
  base: {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4',
      backgroundColor: 'gray.100',
    },
    information: {
      spaceY: '3',
      borderTop: '[4px solid]',
      borderTopColor: 'tertiary',
      backgroundColor: 'white',
      paddingX: '3',
      paddingY: '4',
      _pc: {
        spaceY: '4',
        p: '6',
      },
    },
    plan: {
      spaceY: '3',
      rounded: 'default',
      border: 'medium',
      padding: { base: '3', _pc: '4' },
    },
    sub: {
      _sd: { display: 'contents' },
      _pc: {
        divideY: 'thin',
        divideColor: 'gray.200',
        _empty: { display: 'none' },
      },
    },
  },
})

export function Body() {
  const { isLoading, loginProps, onClick } = useBody()

  // コース一覧から予約を表示したときに最上部までスクロールする
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (ref.current === null) {
      return
    }
    if (!isLoading) {
      const modal = ref.current.closest('[data-modal][role=dialog]')
      modal?.scrollTo({ top: 0 })
    }
  }, [isLoading])

  const userInputRef = useRef<HTMLDivElement>(null)
  const scrollToUserInput = useCallback(() => {
    userInputRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  if (isLoading) {
    return <Loading />
  }

  const classes = styles()

  return (
    <>
      {/* ikyu-login の input 要素のクリックが反応しないように IkyuLogin は onClick の対象外としている */}
      <div onClick={onClick} className={classes.container} ref={ref}>
        <div className={classes.information}>
          <RestaurantContents />
          <div className={css({ spaceY: '2', md: { spaceY: '3' } })}>
            <div className={classes.plan}>
              <PlanContents />
              <OptionMenu />
            </div>
            <SeatsSelect />
          </div>
        </div>
        <div ref={userInputRef}>
          <UserInput />
        </div>
        <CancelGuard />
        <AffiliateSiteForm />
        <RequestsAndComments />
        <PointAndDiscountCouponUnavailable onRegistrationLinkClicked={scrollToUserInput} />
        <div className={classes.sub}>
          <DiscountCoupon />
          <IkyuOnlyIkyuPointsUsage />
          <GivenPoints />
          <PayPayPointsUsage />
        </div>
        <IkyuOnlyYidLoginMessage />
        <PaymentMethod />
        <PaymentAmountDetails />
        <Agreement />
        <MailMagazineSubscription />
        <ReservationConfirmation />
      </div>
      <IkyuLogin {...loginProps} />
    </>
  )
}

function useBody() {
  const isLoading = useIsLoading()
  const { props } = useLoginProps()
  const markInteracted = useMarkUserInteracted()
  const onClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      // ../store/specification.md 「入力エラーの表示について」参照
      const target = e.target as HTMLElement
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
        markInteracted()
      }
    },
    [markInteracted]
  )
  return { isLoading, loginProps: props, onClick }
}
